import type {
  AccountCashOverviewQuery,
  CreateCashTransferRequest,
  UpdateCashTransferRequest,
} from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as cashApi from "@/lib/api/flow-instances/cash";
import { useFlowInstanceGoals } from "./use-goals";

/**
 * QL-287 — cash components of a flow instance. Both reads are instance-wide,
 * so every placement of either component shares one cache entry.
 *
 * They derive from data other components change:
 * - Goals decide which accounts the flow opens and their names. Goals can be
 *   added, renamed or deleted on the SAME page (Goal List, goal details), so
 *   the goal list is part of the query key — any goal change, from any
 *   surface that refreshes the goals query, refetches the cash data.
 * - Portfolios decide what each account buys or sells. They are edited on
 *   portfolio pages, which never host these components, so both queries
 *   refetch on every mount (staleTime 0) when the advisor comes back.
 */
export const cashQueryKeys = {
  transfers: (instanceId: string) => ["flowInstances", instanceId, "cashTransfers"] as const,
  overview: (instanceId: string) => ["flowInstances", instanceId, "accountCashOverview"] as const,
};

/** Goal ids and names — changes whenever a goal is added, renamed or deleted. */
function useGoalsSignature(instanceId: string): string {
  const { goals } = useFlowInstanceGoals(instanceId);
  return goals.map((g: { id: string; name: string }) => `${g.id}:${g.name}`).join("|");
}

export function useCashTransfers(instanceId: string) {
  const goalsSignature = useGoalsSignature(instanceId);
  const { data, isLoading, error } = useQuery({
    queryKey: [...cashQueryKeys.transfers(instanceId), goalsSignature],
    queryFn: () => cashApi.getCashTransfers(instanceId),
    staleTime: 0,
  });
  return { data, isLoading, error };
}

/**
 * `excludeTransferId` (QL-539) is the transfer form's what-if read: the
 * projection without the transfer being edited. It shares the overview key
 * prefix, so every cash invalidation refreshes it too.
 */
export function useAccountCashOverview(instanceId: string, query: AccountCashOverviewQuery = {}) {
  const goalsSignature = useGoalsSignature(instanceId);
  const excludeTransferId = query.excludeTransferId ?? null;
  const { data, isLoading, error } = useQuery({
    queryKey: [...cashQueryKeys.overview(instanceId), goalsSignature, excludeTransferId],
    queryFn: () =>
      cashApi.getAccountCashOverview(instanceId, {
        excludeTransferId: excludeTransferId ?? undefined,
      }),
    staleTime: 0,
  });
  return { data, isLoading, error };
}

/** Every transfer change moves cash between accounts: refresh the list and the overview. */
function useInvalidateCash(instanceId: string) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: cashQueryKeys.transfers(instanceId) });
    queryClient.invalidateQueries({ queryKey: cashQueryKeys.overview(instanceId) });
  };
}

export function useCreateCashTransfer(instanceId: string) {
  const invalidate = useInvalidateCash(instanceId);
  return useMutation({
    mutationFn: (data: CreateCashTransferRequest) => cashApi.createCashTransfer(instanceId, data),
    onSuccess: invalidate,
  });
}

export function useUpdateCashTransfer(instanceId: string) {
  const invalidate = useInvalidateCash(instanceId);
  return useMutation({
    mutationFn: ({ transferId, data }: { transferId: string; data: UpdateCashTransferRequest }) =>
      cashApi.updateCashTransfer(transferId, data),
    onSuccess: invalidate,
  });
}

export function useDeleteCashTransfer(instanceId: string) {
  const invalidate = useInvalidateCash(instanceId);
  return useMutation({
    mutationFn: (transferId: string) => cashApi.deleteCashTransfer(transferId),
    onSuccess: invalidate,
  });
}
